import type { FunctionComponent, HTMLProps, ReactNode } from 'react'
import React, { useEffect, useRef } from 'react'

import classnames from 'classnames'
import FocusTrap from 'focus-trap-react'

import { Grid, GridItem } from '../Grids/Basic'
import { CloseButton } from '.'
import styles from './Modal.module.scss'

export const Modal: FunctionComponent<ModalProps> = ({
  children,
  className,
  closeModal,
  'aria-label': ariaLabel = 'Dialog',
  ...rest
}) => {
  const modalContentRef = useRef<HTMLDivElement>(null)

  const handleEscapeKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      closeModal()
    }
  }

  const handleClickOutsideModalContent = (e: MouseEvent) => {
    if (
      /* istanbul ignore next */
      modalContentRef.current?.contains(e.target as Node)
    ) {
      return
    }
    closeModal()
  }

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleEscapeKey)
    document.addEventListener('mousedown', handleClickOutsideModalContent)
    return () => {
      document.removeEventListener('keydown', handleEscapeKey)
      document.removeEventListener('mousedown', handleClickOutsideModalContent)
      document.body.style.overflow = 'unset'
    }
  })

  return (
    <div
      data-testid="modalContainer"
      className={classnames(styles.modalContainer, className)}
      {...rest}
    >
      <Grid className={styles.grid}>
        <GridItem className={styles.gridItem}>
          <FocusTrap
            focusTrapOptions={{
              tabbableOptions: {
                displayCheck: 'none',
              },
            }}
          >
            <div
              data-testid="modalContent"
              role="dialog"
              aria-modal="true"
              aria-label={ariaLabel}
              ref={modalContentRef}
              className={styles.modalContent}
            >
              <div className={styles.contentWrapper}>{children}</div>
              <CloseButton callback={closeModal} />
            </div>
          </FocusTrap>
        </GridItem>
      </Grid>
    </div>
  )
}

///////IMPLEMENTATION///////

type ModalProps = {
  /**
   * Provided content for the modal
   */
  children: ReactNode

  /**
   * Callback used to set the parent decision to show the modal to false
   */
  closeModal: () => void
  /**
   * Optional className
   */
  className?: string
  /**
   * Optional (recommended) aria-label to be applied to the dialog element
   */
  'aria-label'?: string
} & HTMLProps<HTMLDivElement>
